import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    TextField,
    Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useSelector } from 'react-redux';
import { boostCampaignReport } from '../../../redux/buzzbridge/actions';
import { activeBrandDashboardBrandSelector } from '../../../redux/buzzbridge/selectors';
import type {
    IBoostCampaignReportResult,
    ICampaignReportContentItem,
} from '../../../types/global/campaignReport.types';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { useDispatchPromise } from '../../../utils/AppUtils';
import {
    getAdHubCampaignReviewUrl,
    getCampaignReportBoostDisabledReason,
    isCampaignReportBoostable,
} from '../../../utils/campaignReportBoost';
import { platformLabel } from '../../../utils/campaignReportFormat';
import { BrandAdHubMetaConnectSection } from '../adhub/BrandAdHubMetaConnectSection';
import {
    BRAND_LUXE_PRIMARY,
    BRAND_ON_PRIMARY_FIXED,
    BRAND_OUTLINE_VARIANT,
    BRAND_PRIMARY_CONTAINER,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
} from '../brandDashboardTokens';

const MIN_BUDGET_CENTS = 100;

export interface CampaignBoostModalProps {
    open: boolean;
    onClose: () => void;
    campaignId: string;
    item: ICampaignReportContentItem | null;
}

function adHubCampaignId(result: IBoostCampaignReportResult): string | null {
    const id = result.adhub_campaign?.id || result.adhub_campaign?._id;
    return id ? String(id) : null;
}

export function CampaignBoostModal({ open, onClose, campaignId, item }: CampaignBoostModalProps) {
    const dispatchPromise = useDispatchPromise();
    const activeBrand = useSelector(activeBrandDashboardBrandSelector);
    const [budgetDollars, setBudgetDollars] = useState('5');
    const [linkUrl, setLinkUrl] = useState('');
    const [name, setName] = useState('');
    const [headline, setHeadline] = useState('');
    const [primaryText, setPrimaryText] = useState('');
    const [audienceId, setAudienceId] = useState('');
    const [lookalikeAudienceId, setLookalikeAudienceId] = useState('');
    const [autoSync, setAutoSync] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<IBoostCampaignReportResult | null>(null);
    const [metaReady, setMetaReady] = useState(false);

    useEffect(() => {
        if (!open || !item) return;
        setBudgetDollars('5');
        setLinkUrl(item.live_url || '');
        setName(`Boost: ${item.creator_name}`);
        setHeadline('');
        setPrimaryText('');
        setAudienceId('');
        setLookalikeAudienceId('');
        setAutoSync(false);
        setSubmitting(false);
        setResult(null);
        setMetaReady(false);
    }, [open, item]);

    const budgetCents = useMemo(() => {
        const dollars = Number.parseFloat(budgetDollars);
        if (!Number.isFinite(dollars) || dollars <= 0) return 0;
        return Math.round(dollars * 100);
    }, [budgetDollars]);

    const boostBlockedReason = item ? getCampaignReportBoostDisabledReason(item) : 'Select a post to boost';
    const canSubmit =
        Boolean(item) &&
        isCampaignReportBoostable(item!) &&
        metaReady &&
        budgetCents >= MIN_BUDGET_CENTS &&
        !submitting;

    const handleSubmit = async () => {
        if (!item || !canSubmit) return;
        setSubmitting(true);
        try {
            const data = (await dispatchPromise(
                boostCampaignReport({
                    campaignId,
                    submission_id: item.submission_id,
                    metric_id: item.id,
                    budget_cents: budgetCents,
                    link_url: linkUrl.trim() || undefined,
                    name: name.trim() || undefined,
                    headline: headline.trim() || undefined,
                    primary_text: primaryText.trim() || undefined,
                    audience_id: audienceId.trim() || undefined,
                    lookalike_audience_id: lookalikeAudienceId.trim() || undefined,
                    ad_type: 'normal',
                    auto_sync: autoSync,
                }),
            )) as IBoostCampaignReportResult;
            setResult(data);
        } catch {
            // Toast handled in saga
        } finally {
            setSubmitting(false);
        }
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            color: '#fff',
            bgcolor: BRAND_SURFACE_CONTAINER,
            '& fieldset': { borderColor: `${BRAND_OUTLINE_VARIANT}66` },
        },
        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.55)' },
        mb: 2,
    };

    const resultCampaignId = result ? adHubCampaignId(result) : null;
    const adHubReviewUrl = resultCampaignId ? getAdHubCampaignReviewUrl(resultCampaignId) : null;

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    backgroundImage: 'none',
                    borderRadius: 3,
                    border: `1px solid ${BRAND_OUTLINE_VARIANT}`,
                },
            }}>
            <DialogTitle
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    fontSize: 20,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                }}>
                <BoltIcon sx={{ color: BRAND_LUXE_PRIMARY }} />
                {result ? 'Reels boost draft ready' : 'Boost as Meta Reels'}
            </DialogTitle>
            <DialogContent>
                {item ? (
                    <Box
                        sx={{
                            mb: 2.5,
                            p: 2,
                            borderRadius: 2,
                            bgcolor: BRAND_SURFACE_CONTAINER,
                            border: `1px solid ${BRAND_OUTLINE_VARIANT}44`,
                        }}>
                        <Typography
                            sx={{
                                fontSize: 12,
                                color: 'rgba(255,255,255,0.5)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                            }}>
                            Selected post
                        </Typography>
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', mt: 0.5 }}>
                            {item.creator_name} · {platformLabel(String(item.platform))}
                        </Typography>
                        {item.deliverable_type ? (
                            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', mt: 0.25 }}>
                                {item.deliverable_type.replace(/_/g, ' ')}
                            </Typography>
                        ) : null}
                        {item.live_url ? (
                            <Typography
                                component="a"
                                href={item.live_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    display: 'block',
                                    mt: 0.5,
                                    fontSize: 12,
                                    color: BRAND_LUXE_PRIMARY,
                                    textDecoration: 'none',
                                    wordBreak: 'break-all',
                                }}>
                                {item.live_url}
                            </Typography>
                        ) : null}
                    </Box>
                ) : null}

                {!result ? (
                    <Box sx={{ mb: 2.5 }}>
                        <BrandAdHubMetaConnectSection brandId={activeBrand?._id} compact onReadyChange={setMetaReady} />
                    </Box>
                ) : null}

                {result ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>
                            A Meta AdHub draft was created from this submission video (
                            {result.creative_format || 'reels'} · Facebook Reels + Instagram Reels). Review in AdHub,
                            then sync and activate spend when ready.
                        </Typography>
                        {result.sync_hint ? (
                            <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                                {result.sync_hint}
                            </Typography>
                        ) : null}
                        {result.activate_hint ? (
                            <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                                {result.activate_hint}
                            </Typography>
                        ) : null}
                        {resultCampaignId ? (
                            <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
                                AdHub campaign: {resultCampaignId}
                            </Typography>
                        ) : null}
                        {adHubReviewUrl ? (
                            <Button
                                component="a"
                                href={adHubReviewUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                startIcon={<OpenInNewIcon />}
                                sx={{
                                    alignSelf: 'flex-start',
                                    mt: 0.5,
                                    textTransform: 'none',
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: BRAND_ON_PRIMARY_FIXED,
                                    background: `linear-gradient(90deg, ${BRAND_LUXE_PRIMARY} 0%, ${BRAND_PRIMARY_CONTAINER} 100%)`,
                                    borderRadius: 999,
                                    px: 2.5,
                                }}>
                                Open in AdHub
                            </Button>
                        ) : (
                            <Alert
                                severity="info"
                                sx={{
                                    bgcolor: `${BRAND_LUXE_PRIMARY}14`,
                                    color: 'rgba(255,255,255,0.75)',
                                    '& .MuiAlert-icon': { color: BRAND_LUXE_PRIMARY },
                                }}>
                                Next: open AdHub → sync this draft to Meta → activate spend. Brand Meta must be
                                connected for client placement.
                            </Alert>
                        )}
                    </Box>
                ) : (
                    <>
                        {boostBlockedReason ? (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {boostBlockedReason}
                            </Alert>
                        ) : !metaReady ? (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Connect Meta (ad account + Facebook Page ID) above before creating a Reels draft.
                            </Alert>
                        ) : (
                            <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', mb: 2, lineHeight: 1.5 }}>
                                Uses the submission S3 video for Meta Reels on your connected ad account.
                            </Typography>
                        )}
                        <TextField
                            fullWidth
                            label="Daily budget (USD)"
                            value={budgetDollars}
                            onChange={e => setBudgetDollars(e.target.value)}
                            helperText="Minimum $1 / day"
                            FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)' } }}
                            sx={fieldSx}
                        />
                        <TextField
                            fullWidth
                            label="Destination link"
                            value={linkUrl}
                            onChange={e => setLinkUrl(e.target.value)}
                            placeholder="https://brand.example/landing"
                            sx={fieldSx}
                        />
                        <TextField
                            fullWidth
                            label="Campaign name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            sx={fieldSx}
                        />
                        <TextField
                            fullWidth
                            label="Headline (optional)"
                            value={headline}
                            onChange={e => setHeadline(e.target.value)}
                            sx={fieldSx}
                        />
                        <TextField
                            fullWidth
                            label="Primary text (optional)"
                            value={primaryText}
                            onChange={e => setPrimaryText(e.target.value)}
                            multiline
                            minRows={2}
                            sx={fieldSx}
                        />
                        <TextField
                            fullWidth
                            label="AdHub audience ID (optional)"
                            value={audienceId}
                            onChange={e => setAudienceId(e.target.value)}
                            helperText="Must be platform-synced before auto-sync"
                            FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.4)' } }}
                            sx={fieldSx}
                        />
                        <TextField
                            fullWidth
                            label="Lookalike audience ID (optional)"
                            value={lookalikeAudienceId}
                            onChange={e => setLookalikeAudienceId(e.target.value)}
                            sx={fieldSx}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={autoSync}
                                    onChange={e => setAutoSync(e.target.checked)}
                                    sx={{ color: BRAND_LUXE_PRIMARY, '&.Mui-checked': { color: BRAND_LUXE_PRIMARY } }}
                                />
                            }
                            label={
                                <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>
                                    Auto-sync to Meta (paused)
                                </Typography>
                            }
                        />
                    </>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                <Button
                    onClick={onClose}
                    disabled={submitting}
                    sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.65)' }}>
                    {result ? 'Close' : 'Cancel'}
                </Button>
                {!result ? (
                    <Button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        sx={{
                            px: 3,
                            py: 1.1,
                            borderRadius: 999,
                            textTransform: 'none',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: BRAND_ON_PRIMARY_FIXED,
                            background: `linear-gradient(90deg, ${BRAND_LUXE_PRIMARY} 0%, ${BRAND_PRIMARY_CONTAINER} 100%)`,
                            '&.Mui-disabled': { color: 'rgba(0,0,0,0.4)', opacity: 0.5 },
                        }}>
                        {submitting ? (
                            <CircularProgress size={18} sx={{ color: BRAND_ON_PRIMARY_FIXED }} />
                        ) : (
                            'Create Reels draft'
                        )}
                    </Button>
                ) : null}
            </DialogActions>
        </Dialog>
    );
}
